import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Loader2, Plus, Pencil, Tv, Ghost, Pizza, ShoppingBag, CreditCard, Music, Cloud, Dumbbell } from "lucide-react";
import { toast } from "sonner";

const ICON_OPTIONS = [
  { value: "Tv", label: "TV / Streaming", Icon: Tv },
  { value: "Ghost", label: "Gym / Forgotten", Icon: Ghost },
  { value: "Pizza", label: "Food delivery", Icon: Pizza },
  { value: "ShoppingBag", label: "Shopping", Icon: ShoppingBag },
  { value: "Music", label: "Music", Icon: Music },
  { value: "Cloud", label: "Cloud / SaaS", Icon: Cloud },
  { value: "Dumbbell", label: "Fitness", Icon: Dumbbell },
  { value: "CreditCard", label: "Other", Icon: CreditCard },
];

const COLOR_OPTIONS = [
  { value: "midnight", label: "Midnight" },
  { value: "gold", label: "Champion gold" },
  { value: "red", label: "Danger red" },
];

const EMPTY = { name: "", monthly_cost: "", icon: "CreditCard", color: "midnight", days_until_billing: 14 };

/**
 * Two modes:
 *  - Create: pass `onCreated`. Renders 'New Battle' UI and POSTs /subscriptions.
 *  - Edit: pass `editing` (a subscription) and `onSaved`. PATCHes /subscriptions/{id}.
 *  Optional `open` + `onOpenChange` make the dialog fully controlled (used by the card menu).
 */
export default function AddSubscriptionDialog({
  children,
  onCreated,
  onSaved,
  onLimitReached,
  editing = null,
  open: controlledOpen,
  onOpenChange,
}) {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    if (isControlled) onOpenChange?.(v);
    else setInternalOpen(v);
  };
  const isEdit = Boolean(editing);

  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm({
          name: editing.name || "",
          monthly_cost: String(editing.monthly_cost ?? ""),
          icon: editing.icon || "CreditCard",
          color: editing.color || "midnight",
          days_until_billing: editing.days_until_billing ?? 14,
        });
      } else {
        setForm(EMPTY);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, editing?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const cost = parseFloat(form.monthly_cost);
    if (!form.name.trim() || Number.isNaN(cost) || cost < 0) {
      toast.error("Add a name and a valid monthly cost.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        icon: form.icon,
        monthly_cost: cost,
        days_until_billing: Number(form.days_until_billing) || 14,
        color: form.color,
      };
      if (isEdit) {
        const { data } = await api.patch(`/subscriptions/${editing.id}`, body);
        toast.success(`${data.name} updated.`);
        onSaved?.(data);
      } else {
        const { data } = await api.post("/subscriptions", body);
        toast.success(`${data.name} added to your battles.`);
        onCreated?.(data);
      }
      setOpen(false);
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      if (e2.response?.status === 402 && detail?.code === "SUB_LIMIT_REACHED") {
        setOpen(false);
        onLimitReached?.(detail);
        return;
      }
      const msg = typeof detail === "string" ? detail : detail?.message;
      toast.error(msg || (isEdit ? "Could not save changes." : "Could not add the battle."));
    } finally {
      setBusy(false);
    }
  };

  const Trigger = children ? <DialogTrigger asChild>{children}</DialogTrigger> : null;

  return (
    <Dialog open={open} onOpenChange={(v) => setOpen(v)}>
      {Trigger}
      <DialogContent
        className="sm:max-w-md rounded-3xl border-2 border-slate-100"
        data-testid={isEdit ? "edit-subscription-modal" : "add-subscription-modal"}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-black tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {isEdit ? <Pencil className="inline w-5 h-5 mr-1 text-amber-500" /> : <Plus className="inline w-5 h-5 mr-1 text-amber-500" />}
            {isEdit ? "Edit Battle" : "New Battle"}
          </DialogTitle>
          <DialogDescription className="font-medium text-slate-500">
            {isEdit ? "Update the details below." : "Track another monthly drain so you can defeat it."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label>
            <Input
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. The Cloud Drive Curse" className="h-11 rounded-xl" maxLength={80}
              data-testid="new-sub-name-input" required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Monthly cost</Label>
              <Input
                type="number" step="0.01" min="0" value={form.monthly_cost}
                onChange={(e) => setForm({ ...form, monthly_cost: e.target.value })}
                placeholder="14.99" className="h-11 rounded-xl"
                data-testid="new-sub-cost-input" required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Days until next charge</Label>
              <Input
                type="number" min="0" max="365" value={form.days_until_billing}
                onChange={(e) => setForm({ ...form, days_until_billing: e.target.value })}
                className="h-11 rounded-xl" data-testid="new-sub-days-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Icon</Label>
              <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="new-sub-icon-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map(({ value, label, Icon }) => (
                    <SelectItem key={value} value={value}>
                      <span className="inline-flex items-center gap-2">
                        <Icon className="w-4 h-4" /> {label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Theme</Label>
              <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="new-sub-color-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLOR_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="submit" disabled={busy}
              className="w-full h-12 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black tracking-wide"
              data-testid="new-sub-submit-button"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save changes" : "Add to battles")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
